import {
  AudioVideoComponent,
  ButtonComponent,
  CarouselComponent,
  ColumnComponent,
  DividerComponent,
  EmbedComponent,
  FileComponent,
  FileUploaderComponent,
  FillBlankComponent,
  FlipCardComponent,
  FreeTextComponent,
  FullwidthImageComponent,
  FullwidthVideoComponent,
  GalleryComponent,
  HotspotComponent,
  IconTextComponent,
  ImageComponent,
  InteractiveComponent,
  LessonComponent,
  LessonComponentResponseType,
  ListComponent,
  ScreenCaptureComponent,
  SelectionComponent,
  SequencerComponent,
  SliderComponent,
  SorterComponent,
  TableComponent,
  TextComponent,
  TimelineComponent
} from '../models/LessonComponent';

import { AudioVideo } from './lesson_components/AudioVideo';
import { ButtonComponentView } from './lesson_components/ButtonComponent';
import Carousel from './lesson_components/Carousel';
import Column from './lesson_components/column/Column';
import DividerComponentView from './lesson_components/DividerComponent';
import EmbedComponentView from './lesson_components/EmbedComponent';
import { File } from './lesson_components/File';
import { FileUploader } from './lesson_components/FileUploader';
import FillBlank from './lesson_components/fill_the_blank/FillBlank';
import { FlipCard } from './lesson_components/flip_card/FlipCard';
import { FreeText } from './lesson_components/FreeText';
import FullwidthImage from './lesson_components/FullwidthImage';
import { Gallery } from './lesson_components/gallery/Gallery';
import Hotspot from './lesson_components/Hotspot';
import { IconText } from './lesson_components/icon_text/IconText';
import { Image } from './lesson_components/Image';
import { LessonComponentContent } from '../models/LessonComponentContent';
import { LessonComponentSettings } from '../models/LessonComponentSettings';
import { List } from './lesson_components/list/List';
import React from 'react';
import { ScreenCapture } from './lesson_components/ScreenCapture';
import { ScreenCaptureComparison } from './lesson_components/ScreenCaptureComparison';
import { SelectionCard } from './lesson_components/selection_card/SelectionCard';
import { SequencerSelection } from './lesson_components/SequencerSelection';
import { Slider } from './lesson_components/Slider';
import { Sorter } from './lesson_components/sorter/Sorter';
import { Table } from './lesson_components/table/Table';
import { TextComponentView } from './lesson_components/TextComponent';
import Timeline from './lesson_components/Timeline/Timeline';
import WithMedia from './lesson_components/WithMedia';
import WithQuestion from './lesson_components/WithQuestion';
import { isEqual } from 'lodash';

export interface LessonComponentContentProps {
  component: LessonComponent<LessonComponentSettings, LessonComponentContent>;
  response: LessonComponentResponseType;
  onChange: (response: LessonComponentResponseType) => void;
  onSubmit?: (responsePattern: string) => void; //* used just for the Video component, which submits the progress data directly.
}

export const LessonComponentContentView = React.memo(
  (props: LessonComponentContentProps) => {
    const type = props.component?.type;
    switch (type) {
      case 'heading':
        return (
          <TextComponentView
            styleType="h1"
            component={props.component as TextComponent}
            placeholder="Click to enter heading text"
          />
        );
      case 'subheading':
        return (
          <TextComponentView
            styleType="h2"
            component={props.component as TextComponent}
            placeholder="Click to enter subheading text"
          />
        );
      case 'paragraph':
        return (
          <TextComponentView
            styleType="p"
            component={props.component as TextComponent}
            placeholder="Click to enter paragraph text"
          />
        );
      case 'list':
        return (
          <List
            styleType="noList"
            component={props.component as ListComponent}
            placeholder="Click to enter text"
          />
        );
      case 'table':
        return (
          <Table
            styleType="noLinkList"
            component={props.component as TableComponent}
          />
        );
      case 'single_selection':
      case 'multiple_selection':
        return <WithMedia {...props} />;
      case 'single_selection_card':
      case 'multiple_selection_card':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <SelectionCard
              component={props.component as SelectionComponent}
              response={props.response}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'free_text':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <FreeText
              component={props.component as FreeTextComponent}
              response={props.response}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'button':
        return (
          <ButtonComponentView component={props.component as ButtonComponent} />
        );
      case 'embed':
        return (
          <EmbedComponentView component={props.component as EmbedComponent} />
        );
      case 'divider':
        return (
          <DividerComponentView
            component={props.component as DividerComponent}
          />
        );
      case 'image':
        return <Image component={props.component as ImageComponent} />;
      case 'video':
      case 'audio':
        return (
          <AudioVideo
            component={props.component as AudioVideoComponent}
            onSubmit={props.onSubmit}
          />
        );
      case 'file':
        return <File component={props.component as FileComponent} />;
      case 'sorter':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <Sorter
              component={props.component as SorterComponent}
              response={props.response}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'full_width_image':
        return (
          <FullwidthImage
            component={props.component as FullwidthImageComponent}
          />
        );
      case 'full_width_video':
        return (
          <AudioVideo
            component={props.component as FullwidthVideoComponent}
            onSubmit={props.onSubmit}
          />
        );
      case 'carousel':
        return (
          <Carousel
            component={props.component as CarouselComponent}
            onSubmit={props.onSubmit}
          />
        );
      case 'hotspot':
        return (
          <Hotspot
            component={props.component as HotspotComponent}
            onSubmit={props.onSubmit}
          />
        );
      case 'sequencer':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <SequencerSelection
              component={props.component as SequencerComponent}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'slider':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <Slider
              component={props.component as SliderComponent}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'fill_blank':
      case 'fill_menu':
        return (
          <WithQuestion component={props.component as FillBlankComponent}>
            <FillBlank
              component={props.component as FillBlankComponent}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'video_uploader':
      case 'file_uploader':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <FileUploader
              component={props.component as FileUploaderComponent}
              category="learner"
              onSubmit={props.onSubmit}
            />
          </WithQuestion>
        );
      case 'screen_capture':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <ScreenCapture
              component={props.component as ScreenCaptureComponent}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'screen_capture_comparison':
        return (
          <WithQuestion component={props.component as InteractiveComponent}>
            <ScreenCaptureComparison
              component={props.component as ScreenCaptureComponent}
              onChange={props.onChange}
            />
          </WithQuestion>
        );
      case 'flip_card':
        return (
          <FlipCard
            component={props.component as FlipCardComponent}
            onSubmit={props.onSubmit}
          />
        );
      case 'column':
        return <Column component={props.component as ColumnComponent} />;
      case 'icon_text':
        return <IconText component={props.component as IconTextComponent} />;
      case 'gallery':
        return <Gallery component={props.component as GalleryComponent} />;
      case 'timeline':
        return <Timeline component={props.component as TimelineComponent} />;
      default:
        return null;
    }
  },
  (prev, next) => isEqual(prev.component, next.component)
);
